import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Plus, Monitor, Megaphone, Users, Building, Plane, Tag, MoreHorizontal, X, Trash2, Pencil } from 'lucide-react'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import { useDateRange } from '../context/DateRangeContext'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { getExpenses, createExpense as createExpenseSvc, updateExpense as updateExpenseSvc, deleteExpense as deleteExpenseSvc } from '../services/expenses.service'
import type { Expense } from '../types'

// ── Add Expense Modal ──────────────────────────────────────────
function AddExpenseModal({ onClose, onAdd }: { onClose: () => void; onAdd: (e: Omit<Expense, 'id'>) => void }) {
  const today = new Date().toISOString().split('T')[0]
  const [form, setForm] = useState({
    description: '', category: 'Software' as Expense['category'],
    amount: '', vendor: '', date: today, approvedBy: 'HJ',
  })
  const [error, setError] = useState('')

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.description.trim()) { setError('Description is required.'); return }
    const amt = parseFloat(form.amount.replace(/[^0-9.]/g, ''))
    if (!amt || amt <= 0) { setError('Enter a valid amount.'); return }
    onAdd({
      description: form.description.trim(),
      category:    form.category,
      amount:      amt,
      vendor:      form.vendor.trim() || '—',
      date:        form.date,
      approvedBy:  form.approvedBy.trim() || undefined,
    })
    onClose()
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Add Expense</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DESCRIPTION *</label>
            <input value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="e.g. Figma Pro renewal" className={inputCls} style={inputStyle} autoFocus
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CATEGORY</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls} style={inputStyle}>
                {(['Software','Advertising','Salaries','Office','Travel','Marketing','Misc'] as const).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>AMOUNT (₹) *</label>
              <input value={form.amount} onChange={e => set('amount', e.target.value)}
                placeholder="e.g. 12000" className={inputCls} style={inputStyle}
                onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>VENDOR</label>
              <input value={form.vendor} onChange={e => set('vendor', e.target.value)}
                placeholder="e.g. Google" className={inputCls} style={inputStyle}
                onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DATE</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }}
                onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>APPROVED BY</label>
            <input value={form.approvedBy} onChange={e => set('approvedBy', e.target.value)}
              placeholder="e.g. HJ" className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              Add Expense
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Edit Expense Modal ─────────────────────────────────────────
function EditExpenseModal({ expense, onClose, onSave }: { expense: Expense; onClose: () => void; onSave: (updated: Expense) => void }) {
  const [form, setForm] = useState({
    description: expense.description,
    category:    expense.category,
    amount:      String(expense.amount),
    vendor:      expense.vendor === '—' ? '' : expense.vendor,
    date:        expense.date,
    approvedBy:  expense.approvedBy ?? '',
  })
  const [error, setError] = useState('')

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.description.trim()) { setError('Description is required.'); return }
    const amt = parseFloat(form.amount.replace(/[^0-9.]/g, ''))
    if (!amt || amt <= 0) { setError('Enter a valid amount.'); return }
    onSave({
      ...expense,
      description: form.description.trim(),
      category:    form.category as Expense['category'],
      amount:      amt,
      vendor:      form.vendor.trim() || '—',
      date:        form.date,
      approvedBy:  form.approvedBy.trim() || undefined,
    })
    onClose()
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }
  const focusOn  = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')
  const focusOff = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Edit Expense</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DESCRIPTION *</label>
            <input value={form.description} onChange={e => set('description', e.target.value)}
              className={inputCls} style={inputStyle} autoFocus
              onFocus={focusOn} onBlur={focusOff} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CATEGORY</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls} style={inputStyle}>
                {(['Software','Advertising','Salaries','Office','Travel','Marketing','Misc'] as const).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>AMOUNT (₹) *</label>
              <input value={form.amount} onChange={e => set('amount', e.target.value)}
                className={inputCls} style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>VENDOR</label>
              <input value={form.vendor} onChange={e => set('vendor', e.target.value)}
                className={inputCls} style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DATE</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }}
                onFocus={focusOn} onBlur={focusOff} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>APPROVED BY</label>
            <input value={form.approvedBy} onChange={e => set('approvedBy', e.target.value)}
              className={inputCls} style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

type Category = Expense['category'] | 'All'
const CATEGORIES: Expense['category'][] = ['Software', 'Advertising', 'Salaries', 'Office', 'Travel', 'Marketing', 'Misc']

const CATEGORY_META: Record<Expense['category'], { icon: React.ElementType; color: string }> = {
  Software: { icon: Monitor, color: '#ffc600' },
  Advertising: { icon: Megaphone, color: '#e8a000' },
  Salaries: { icon: Users, color: '#ff8c00' },
  Office: { icon: Building, color: '#a0a0a0' },
  Travel: { icon: Plane, color: '#555555' },
  Marketing: { icon: Tag, color: '#fff0a0' },
  Misc: { icon: MoreHorizontal, color: '#555555' },
}

const PIE_COLORS = ['#ffc600', '#e8a000', '#ff8c00', '#a0a0a0', '#555555', '#fff0a0', '#3a3a3a']

function formatINR(n: number) {
  return '₹' + n.toLocaleString('en-IN')
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg p-3 text-xs" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.25)', color: '#fff' }}>
      <p className="font-semibold">{payload[0].name}</p>
      <p style={{ color: '#ffc600' }}>{formatINR(payload[0].value)}</p>
    </div>
  )
}

export default function Expenses() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [filter, setFilter]     = useState<Category>('All')
  const [sortBy, setSortBy]     = useState<'date' | 'amount'>('date')
  const [showAdd, setShowAdd]   = useState(false)
  const [editing, setEditing]   = useState<Expense | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const { toast }               = useToast()
  const { addActivity }         = useActivity()
  const isAdmin                 = useIsAdmin()
  const { inRange }             = useDateRange()
  const location                = useLocation()

  useEffect(() => {
    getExpenses().then(setExpenses).catch(console.error)
    if ((location.state as any)?.openAdd) {
      setShowAdd(true)
      window.history.replaceState({}, '')
    }
  }, [])

  const handleSaveEdit = async (updated: Expense) => {
    try {
      await updateExpenseSvc(updated.id, updated)
      setExpenses(prev => prev.map(e => e.id === updated.id ? updated : e))
      toast(`Expense "${updated.description.slice(0, 30)}" updated`)
    } catch { toast('Update failed', 'error') }
  }

  const handleDelete = async (id: string) => {
    const exp = expenses.find(e => e.id === id)
    try {
      await deleteExpenseSvc(id)
      setExpenses(prev => prev.filter(e => e.id !== id))
      setDeleting(null)
      if (exp) toast(`"${exp.description.slice(0, 30)}" deleted`, 'error')
    } catch { toast('Delete failed', 'error') }
  }

  const dateExpenses = expenses.filter(e => inRange(e.date))
  const filtered = dateExpenses
    .filter(e => filter === 'All' || e.category === filter)
    .sort((a, b) => sortBy === 'date'
      ? new Date(b.date).getTime() - new Date(a.date).getTime()
      : b.amount - a.amount)

  const totalThisMonth = dateExpenses.reduce((s, e) => s + e.amount, 0)

  const categoryTotals = CATEGORIES.map(cat => ({
    name: cat,
    value: dateExpenses.filter(e => e.category === cat).reduce((s, e) => s + e.amount, 0),
    count: dateExpenses.filter(e => e.category === cat).length,
  })).filter(c => c.value > 0)

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">Expenses</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>This Month: <span style={{ color: '#ffc600' }}>{formatINR(totalThisMonth)}</span></p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#ffc600', color: '#000' }}>
            <Plus size={15} /> Add Expense
          </button>
        )}
      </div>

      {/* Category summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {categoryTotals.slice(0, 4).map(cat => {
          const meta = CATEGORY_META[cat.name as Expense['category']]
          const Icon = meta.icon
          return (
            <div key={cat.name} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color: meta.color }} />
                <p className="text-xs font-medium" style={{ color: '#a0a0a0' }}>{cat.name}</p>
              </div>
              <p className="text-lg font-bold font-syne text-white">{formatINR(cat.value)}</p>
              <p className="text-xs mt-0.5" style={{ color: '#555' }}>{cat.count} item{cat.count !== 1 ? 's' : ''}</p>
            </div>
          )
        })}
      </div>

      <div className="flex gap-6 flex-col lg:flex-row mb-6">
        {/* Pie chart */}
        <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)', minWidth: 280 }}>
          <p className="text-sm font-semibold text-white mb-3">Breakdown by Category</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={categoryTotals} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {categoryTotals.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#a0a0a0' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Filters */}
        <div className="flex-1">
          <div className="flex flex-wrap gap-2 mb-4">
            {(['All', ...CATEGORIES] as Category[]).map(c => (
              <button key={c} onClick={() => setFilter(c)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={filter === c ? { background: '#ffc600', color: '#000' } : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                {c}
              </button>
            ))}
            <div className="ml-auto flex gap-2">
              {(['date', 'amount'] as const).map(s => (
                <button key={s} onClick={() => setSortBy(s)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all"
                  style={sortBy === s ? { background: 'rgba(255,198,0,0.15)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.35)' } : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                  Sort by {s}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs mb-2" style={{ color: '#555' }}>{filtered.length} expense{filtered.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.12)' }}>
              {['Date', 'Description', 'Category', 'Vendor', 'Amount', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#a0a0a0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12" style={{ color: '#555', background: '#0f0f0f' }}>No expenses found</td></tr>
            ) : filtered.map(exp => {
              const meta = CATEGORY_META[exp.category]
              const Icon = meta.icon
              return (
                <tr key={exp.id} style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.06)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#0f0f0f')}>
                  <td className="px-4 py-3 text-xs" style={{ color: '#a0a0a0' }}>{new Date(exp.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}</td>
                  <td className="px-4 py-3 font-medium text-white">{exp.description}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-xs px-2 py-0.5 rounded w-fit" style={{ background: 'rgba(255,198,0,0.08)', color: meta.color }}>
                      <Icon size={11} />{exp.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: '#a0a0a0' }}>{exp.vendor}</td>
                  <td className="px-4 py-3 font-semibold text-white">{formatINR(exp.amount)}</td>
                  <td className="px-4 py-3">
                    {isAdmin && (
                      deleting === exp.id ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs" style={{ color: '#a0a0a0' }}>Delete?</span>
                          <button onClick={() => handleDelete(exp.id)}
                            className="text-xs px-2 py-1 rounded font-semibold"
                            style={{ background: 'rgba(204,51,0,0.15)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>
                            Yes
                          </button>
                          <button onClick={() => setDeleting(null)}
                            className="text-xs px-2 py-1 rounded"
                            style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setEditing(exp)}
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-all"
                            style={{ color: '#ffc600', border: '1px solid rgba(255,198,0,0.25)' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,198,0,0.08)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <Pencil size={11} /> Edit
                          </button>
                          <button onClick={() => setDeleting(exp.id)}
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-all"
                            style={{ color: '#555', border: '1px solid rgba(255,255,255,0.06)' }}
                            onMouseEnter={e => { e.currentTarget.style.color = '#cc3300'; e.currentTarget.style.borderColor = 'rgba(204,51,0,0.3)' }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)' }}>
                            <Trash2 size={11} />
                          </button>
                        </div>
                      )
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddExpenseModal
          onClose={() => setShowAdd(false)}
          onAdd={async exp => {
            try {
              const saved = await createExpenseSvc(exp)
              setExpenses(prev => [saved, ...prev])
              toast(`Expense "${saved.description.slice(0,30)}" added`)
              addActivity({ type: 'expense_added', emoji: '🧾', description: `Expense added: ${saved.description.slice(0,30)}`, detail: `${saved.category} · ₹${saved.amount.toLocaleString('en-IN')}` })
            } catch { toast('Failed to add expense', 'error') }
          }}
        />
      )}

      {editing && (
        <EditExpenseModal
          expense={editing}
          onClose={() => setEditing(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  )
}
